import { useCallback, useMemo, useState } from "react";

import * as ModelConverter from "@/components/features/_model/converter";

import * as Factory from "./factory";
import * as FormModelType from "./type";
import * as ValidationSchema from "./validation-schema";

export const usePlainSettingFormModel = (initInputModel: FormModelType.PlainSettingFormModel["InputModel"]) => {
	/**
	 * DefaultInputModel の初期化
	 */
	const defaultInputModel: FormModelType.PlainSettingFormModel["InputModel"] = useMemo(
		() =>
			Factory.Default.plainSettingInputModelFactory(
				initInputModel.hotelID,
			) satisfies FormModelType.PlainSettingFormModel["InputModel"],
		[initInputModel.hotelID],
	);

	/**
	 * CurrentInputModel の初期化
	 */
	const [currentInputModel, setCurrentInputModel] =
		useState<FormModelType.PlainSettingFormModel["InputModel"]>(initInputModel);

	/**
	 * ViewModel の初期化
	 */
	const [viewModel, setViewModel] = useState<FormModelType.PlainSettingFormModel["ViewModel"]>(
		ModelConverter.inputModelToViewModelConverter<
			FormModelType.PlainSettingFormModel["InputModel"],
			FormModelType.ExcludeKeys,
			FormModelType.PlainSettingFormModel["QueryData"],
			ValidationSchema.PlainSettingInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: initInputModel,
			currentInput: initInputModel,
			excludeKeys: FormModelType.excludeKeys,
			zodSchema: ValidationSchema.PlainSettingInputZodSchema,
		}),
	);

	/**
	 * ViewModel を更新する関数。
	 * この関数は useActions でのみ利用される。
	 * @see file://./use-case/use-actions.ts
	 *
	 * DefaultInputModel は ViewModel と比較し、各 Node の変更差分時に利用される。
	 * InitInputModel は ViewModel と lodash.isequal での全体比較時に利用される。
	 * CurrentInputModel は 変更された ViewModel の値を元にバリデーションを実行する際に利用される。
	 * その為、一度 ViewModel -> InputModel に変換し、再度 InputModel -> ViewModel に変換する。
	 */
	const onSetViewModel = useCallback(
		(_viewModel: FormModelType.PlainSettingFormModel["ViewModel"]) => {
			const convertedInputModel = ModelConverter.viewModelToInputModelConverter(_viewModel);
			const convertedViewModel = ModelConverter.inputModelToViewModelConverter<
				FormModelType.PlainSettingFormModel["InputModel"],
				FormModelType.ExcludeKeys,
				FormModelType.PlainSettingFormModel["QueryData"],
				ValidationSchema.PlainSettingInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: initInputModel,
				currentInput: convertedInputModel,
				excludeKeys: FormModelType.excludeKeys,
				zodSchema: ValidationSchema.PlainSettingInputZodSchema,
			});
			// state の更新
			setCurrentInputModel(convertedInputModel);
			setViewModel(convertedViewModel);
		},
		[defaultInputModel, initInputModel],
	);

	/**
	 * ViewModel のリセットを行う関数。
	 * この関数は useUpdatePlainSettingAndWidget でのみ利用される。
	 * @see file://./use-case/use-update/use-update-plain-setting-and-widget.ts
	 */
	const onResetViewModel = useCallback(() => {
		setViewModel({
			...viewModel,
			isDirty: false,
		});
	}, [viewModel]);

	return {
		inputModel: currentInputModel,
		viewModel,
		onSetViewModel,
		onResetViewModel,
	};
};
